import Highcharts from "highcharts";

const dataTableDialogs = new WeakMap();
const allowedCaptionTags = ["B", "BR", "EM", "I", "SPAN", "STRONG", "SUB", "SUP"];

const restoreHighchartsPrintState = () => {
  Highcharts.charts.filter(Boolean).forEach((chart) => {
    if (chart.exporting?.printReverseInfo || chart.exporting?.isPrinting) {
      chart.exporting.afterPrint();
    }
  });
};

const copyAllowedCaptionStyle = (source, target) => {
  if (source.tagName !== "SPAN") {
    return;
  }

  if (source.style.color) {
    target.style.color = source.style.color;
  }

  if (source.style.fontWeight) {
    target.style.fontWeight = source.style.fontWeight;
  }
};

const sanitizeCaptionNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent);
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return document.createDocumentFragment();
  }

  if (["SCRIPT", "STYLE"].includes(node.tagName)) {
    return document.createDocumentFragment();
  }

  const fragment = document.createDocumentFragment();
  const children = Array.from(node.childNodes).map(sanitizeCaptionNode);

  if (!allowedCaptionTags.includes(node.tagName)) {
    children.forEach((child) => fragment.appendChild(child));
    return fragment;
  }

  const element = document.createElement(node.tagName.toLowerCase());
  copyAllowedCaptionStyle(node, element);
  children.forEach((child) => element.appendChild(child));
  return element;
};

const renderCaptionHtml = (dataTable) => {
  dataTable.querySelectorAll("caption.highcharts-table-caption").forEach((caption) => {
    const captionText = caption.textContent || "";

    if (!/<\/?[a-z][\s\S]*>/i.test(captionText)) {
      return;
    }

    const template = document.createElement("template");
    template.innerHTML = captionText;

    caption.replaceChildren(
      ...Array.from(template.content.childNodes).map(sanitizeCaptionNode)
    );
  });
};

const filterDataTable = (dataTable, searchText, matchCount) => {
  const query = searchText.trim().toLowerCase();
  const rows = Array.from(dataTable.querySelectorAll("tbody tr"));
  let visibleRows = 0;

  rows.forEach((row) => {
    const rowText = (row.textContent || "").toLowerCase();
    const isMatch = !query || rowText.includes(query);
    row.style.display = isMatch ? "" : "none";

    if (isMatch) {
      visibleRows += 1;
    }
  });

  matchCount.textContent = query
    ? `${visibleRows} of ${rows.length} rows`
    : `${rows.length} rows`;
};

const getChartForDataTable = (dataTable) =>
  Highcharts.charts
    .filter(Boolean)
    .find(
      (chart) =>
        chart.dataTableDiv === dataTable ||
        chart.exporting?.dataTableDiv === dataTable ||
        chart.renderTo?.contains(dataTable) ||
        chart.container?.parentElement?.contains(dataTable)
    );

const syncDialogVisibility = (dataTable) => {
  const dialog = dataTableDialogs.get(dataTable);
  if (!dialog) {
    return;
  }

  renderCaptionHtml(dataTable);
  filterDataTable(dataTable, dialog.searchInput.value, dialog.matchCount);

  const isVisible = dataTable.style.display !== "none" && !dataTable.hidden;
  dialog.overlay.style.display = isVisible ? "flex" : "none";
  dialog.overlay.setAttribute("aria-hidden", isVisible ? "false" : "true");

  if (isVisible && !dialog.wasVisible) {
    setTimeout(() => dialog.searchInput.focus(), 0);
  }

  dialog.wasVisible = isVisible;
};

const closeDataTableDialog = (dataTable) => {
  const chart = getChartForDataTable(dataTable);

  if (chart?.exporting?.hideData) {
    chart.exporting.hideData();
  } else if (chart?.hideData) {
    chart.hideData();
  } else {
    dataTable.style.display = "none";
  }

  syncDialogVisibility(dataTable);
};

const getExportFileName = (dataTable) => {
  const caption = dataTable.querySelector("caption")?.textContent || "chart-data";
  const safeName = caption
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return safeName || "chart-data";
};

const exportDataTableToExcel = (dataTable) => {
  const table = dataTable.querySelector("table");

  if (!table) {
    return;
  }

  const tableClone = table.cloneNode(true);
  const sourceRows = Array.from(table.querySelectorAll("tbody tr"));
  const clonedRows = Array.from(tableClone.querySelectorAll("tbody tr"));

  clonedRows.forEach((row, index) => {
    if (sourceRows[index]?.style.display === "none") {
      row.remove();
    }
  });

  const html = `<!doctype html><html><head><meta charset="utf-8" /></head><body>${tableClone.outerHTML}</body></html>`;
  const blob = new Blob(["\ufeff", html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });

  if (window.navigator?.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, `${getExportFileName(dataTable)}.xls`);
    return;
  }

  const downloadLink = document.createElement("a");
  const objectUrl = URL.createObjectURL(blob);

  downloadLink.href = objectUrl;
  downloadLink.download = `${getExportFileName(dataTable)}.xls`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  URL.revokeObjectURL(objectUrl);
};

const findDataTables = (node) => {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const element = node;
  const dataTables = element.matches(".highcharts-data-table") ? [element] : [];
  dataTables.push(...element.querySelectorAll(".highcharts-data-table"));
  return dataTables;
};

const decorateDataTable = (dataTable) => {
  renderCaptionHtml(dataTable);

  if (dataTableDialogs.has(dataTable)) {
    syncDialogVisibility(dataTable);
    return;
  }

  const overlay = document.createElement("div");
  overlay.className = "qr-highcharts-data-table-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");

  const panel = document.createElement("div");
  panel.className = "qr-highcharts-data-table-dialog";

  const header = document.createElement("div");
  header.className = "qr-highcharts-data-table-header";

  const title = document.createElement("div");
  title.className = "qr-highcharts-data-table-title";
  title.textContent = "Chart data";

  const headerActions = document.createElement("div");
  headerActions.className = "qr-highcharts-data-table-actions";

  const exportButton = document.createElement("button");
  exportButton.type = "button";
  exportButton.className = "qr-highcharts-data-table-export";
  exportButton.setAttribute("aria-label", "Export chart data table to Excel");

  const exportIcon = document.createElement("span");
  exportIcon.className = "qr-highcharts-data-table-export-icon";
  exportIcon.setAttribute("aria-hidden", "true");

  const exportLabel = document.createElement("span");
  exportLabel.textContent = "Export Excel";

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "qr-highcharts-data-table-close";
  closeButton.setAttribute("aria-label", "Close chart data table");
  closeButton.textContent = "Close";

  const toolbar = document.createElement("div");
  toolbar.className = "qr-highcharts-data-table-toolbar";

  const searchInput = document.createElement("input");
  searchInput.type = "search";
  searchInput.className = "qr-highcharts-data-table-search";
  searchInput.placeholder = "Search table";
  searchInput.setAttribute("aria-label", "Search chart data table");

  const matchCount = document.createElement("span");
  matchCount.className = "qr-highcharts-data-table-count";
  matchCount.setAttribute("aria-live", "polite");

  const body = document.createElement("div");
  body.className = "qr-highcharts-data-table-body";

  exportButton.appendChild(exportIcon);
  exportButton.appendChild(exportLabel);
  headerActions.appendChild(exportButton);
  headerActions.appendChild(closeButton);
  toolbar.appendChild(searchInput);
  toolbar.appendChild(matchCount);
  header.appendChild(title);
  header.appendChild(headerActions);
  panel.appendChild(header);
  panel.appendChild(toolbar);
  panel.appendChild(body);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  body.appendChild(dataTable);

  searchInput.addEventListener("input", () =>
    filterDataTable(dataTable, searchInput.value, matchCount)
  );
  exportButton.addEventListener("click", () => exportDataTableToExcel(dataTable));
  closeButton.addEventListener("click", () => closeDataTableDialog(dataTable));
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) {
      closeDataTableDialog(dataTable);
    }
  });

  const visibilityObserver = new MutationObserver(() => syncDialogVisibility(dataTable));
  visibilityObserver.observe(dataTable, {
    attributes: true,
    attributeFilter: ["style", "hidden", "class"],
  });

  const contentObserver = new MutationObserver(() => {
    renderCaptionHtml(dataTable);
    filterDataTable(dataTable, searchInput.value, matchCount);
  });
  contentObserver.observe(dataTable, {
    childList: true,
    subtree: true,
  });

  dataTableDialogs.set(dataTable, {
    overlay,
    visibilityObserver,
    contentObserver,
    searchInput,
    matchCount,
    wasVisible: false,
  });
  syncDialogVisibility(dataTable);
};

export const setupHighchartsDataTableDialog = () => {
  if (typeof document === "undefined" || window.__qrHighchartsDataTableDialog) {
    return;
  }

  window.__qrHighchartsDataTableDialog = true;

  window.addEventListener("afterprint", restoreHighchartsPrintState);
  window.addEventListener("focus", () => setTimeout(restoreHighchartsPrintState, 0));
  window.addEventListener("pageshow", restoreHighchartsPrintState);

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      return;
    }

    document
      .querySelectorAll(".qr-highcharts-data-table-overlay")
      .forEach((overlay) => {
        if (overlay.style.display !== "none") {
          const dataTable = overlay.querySelector(".highcharts-data-table");
          if (dataTable) {
            closeDataTableDialog(dataTable);
          }
        }
      });
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        findDataTables(node).forEach(decorateDataTable);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
  document.querySelectorAll(".highcharts-data-table").forEach(decorateDataTable);
};